import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { track } from "@/lib/analytics";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.message === "string") {
        throw new Error(parsed.message);
      }
    } catch (e) {
      if (!(e instanceof SyntaxError)) throw e;
    }
    throw new Error(`${res.status}: ${text}`);
  }
}

// ── CSRF token — module-level cache ──────────────────────────────────────────
// Session-lifetime token; cached for the page lifecycle. Tokens don't rotate
// mid-session; a 403 from CSRF rejection indicates a session reset — clearing
// the cache (via page reload) is the correct recovery path.
// The in-flight Promise is also cached so concurrent callers before the first
// response arrives share one fetch rather than firing duplicate requests.
let _csrfToken = "";
let _csrfTokenPromise: Promise<string> | null = null;

export async function getCsrfToken(): Promise<string> {
  if (_csrfToken) return _csrfToken;
  if (_csrfTokenPromise) return _csrfTokenPromise;

  _csrfTokenPromise = (async () => {
    try {
      const res = await fetch("/api/csrf-token", { credentials: "include" });
      const data = await res.json();
      _csrfToken = data.token ?? "";
      return _csrfToken;
    } finally {
      _csrfTokenPromise = null;
    }
  })();

  return _csrfTokenPromise;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    headers["x-csrf-token"] = await getCsrfToken();
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);

  if (
    method !== "GET" &&
    /\/api\/work-orders\/\d+\/documents(?:\/|$)/.test(url)
  ) {
    const payload = (data && typeof data === "object") ? (data as Record<string, unknown>) : undefined;
    const match = url.match(/\/api\/work-orders\/(\d+)\/documents/);
    const workOrderId = match ? parseInt(match[1], 10) : null;

    track("document_request_updated", {
      source_page: typeof window !== "undefined" ? window.location.pathname : null,
      work_order_id: workOrderId,
      document_type: payload?.documentType ?? null,
      submitted: payload?.submitted ?? null,
      action: method.toLowerCase(),
    });
  }

  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
